<template>
  <v-container>
    <!-- Title -->
    <v-row class="mb-1">
      <v-col>
        <div class="text-h5 font-weight-bold">
          ລາຍງານສະຫຼຸບຍອດບິນ
        </div>
        <div class="text-body-2 grey--text">
          ສະຫຼຸບຍອດລວມ / ຈ່າຍແລ້ວ / ຄ້າງ ຕາມຊ່ວງເວລາ
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card
      outlined
      class="mb-4"
    >
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            sm="auto"
          >
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  clearable
                  dense
                  hide-details
                  label="ເລີ່ມວັນທີ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  style="min-width: 170px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="startDate" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="auto"
          >
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  clearable
                  dense
                  hide-details
                  label="ຫາວັນທີ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  style="min-width: 170px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="endDate" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="auto"
          >
            <v-menu
              v-model="billMonthMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="billMonth"
                  clearable
                  dense
                  hide-details
                  hint="ໃຊ້ແທນຊ່ວງວັນທີ"
                  label="ບິນປະຈຳເດືອນ"
                  outlined
                  persistent-hint
                  prepend-inner-icon="mdi-calendar-month"
                  readonly
                  style="min-width: 180px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="billMonth"
                type="month"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="auto"
          >
            <!--
              NOTE: do not add `clearable` here. The customerTypes list contains
              an item with value: null ("ທັງໝົດ"), and the Vuetify 2 v-select has
              a known interaction bug where `clearable` + a null-valued item
              swallows real mouse clicks on the other items (programmatic
              .click() still works, which is why this only shows up to humans).
              The "ທັງໝົດ" item itself is how the user clears the filter, so
              `clearable` is redundant anyway. See PR #92 follow-up.
            -->
            <v-select
              v-model="customerType"
              :items="customerTypes"
              dense
              hide-details
              item-text="text"
              item-value="value"
              label="ປະເພດລູກຄ້າ"
              outlined
              style="min-width: 180px"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-autocomplete
              v-model="districtId"
              :items="districts"
              :loading="loadingDistricts"
              clearable
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="ເມືອງ"
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-autocomplete
              v-model="villageIds"
              :items="villages"
              :loading="loadingVillages"
              :disabled="!districtId"
              chips
              clearable
              deletable-chips
              dense
              hide-details
              item-text="name"
              item-value="id"
              :label="villageIds.length ? 'ບ້ານ (ໃຊ້ແທນຕົວກັ່ນຕອງເມືອງ)' : 'ບ້ານ'"
              multiple
              outlined
              small-chips
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="auto">
            <v-btn
              color="primary"
              depressed
              height="40"
              :loading="loading"
              @click="fetchData"
            >
              <v-icon
                left
                small
              >
                mdi-magnify
              </v-icon>
              ຄົ້ນຫາ
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="success"
              depressed
              height="40"
              :loading="exporting"
              @click="onExport"
            >
              <v-icon
                left
                small
              >
                mdi-microsoft-excel
              </v-icon>
              Export Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPI Tiles -->
    <v-row>
      <v-col
        cols="12"
        sm="4"
      >
        <v-card
          class="rounded-lg"
          outlined
        >
          <v-card-text class="pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-subtitle-1 font-weight-medium">
                ລວມທັງໝົດ
              </span>
              <v-chip
                color="blue"
                dark
                small
              >
                <v-icon
                  left
                  x-small
                >
                  mdi-counter
                </v-icon>
                ລວມ
              </v-chip>
            </div>
            <div class="text-h4 font-weight-bold mb-1">
              {{ formatCurrency(summary.total.amount) }}
              <span class="text-subtitle-1 grey--text font-weight-regular">
                LAK
              </span>
            </div>
            <div class="text-body-2 grey--text">
              {{ formatNumber(summary.total.count) }} ບິນ
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-card
          class="rounded-lg"
          outlined
        >
          <v-card-text class="pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-subtitle-1 font-weight-medium">
                ຈ່າຍແລ້ວ
              </span>
              <v-chip
                color="green"
                dark
                small
              >
                <v-icon
                  left
                  x-small
                >
                  mdi-check-circle
                </v-icon>
                ຈ່າຍ
              </v-chip>
            </div>
            <div class="text-h4 font-weight-bold mb-1 green--text">
              {{ formatCurrency(summary.paid.amount) }}
              <span class="text-subtitle-1 grey--text font-weight-regular">
                LAK
              </span>
            </div>
            <div class="text-body-2 grey--text">
              {{ formatNumber(summary.paid.count) }} ບິນ
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-card
          class="rounded-lg"
          outlined
        >
          <v-card-text class="pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-subtitle-1 font-weight-medium">
                ຄ້າງ
              </span>
              <v-chip
                color="red"
                dark
                small
              >
                <v-icon
                  left
                  x-small
                >
                  mdi-alert-circle
                </v-icon>
                ບໍ່ຈ່າຍ
              </v-chip>
            </div>
            <div class="text-h4 font-weight-bold mb-1 red--text">
              {{ formatCurrency(summary.unpaid.amount) }}
              <span class="text-subtitle-1 grey--text font-weight-regular">
                LAK
              </span>
            </div>
            <div class="text-body-2 grey--text">
              {{ formatNumber(summary.unpaid.count) }} ບິນ
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Export jobs table -->
    <v-card
      class="mt-6"
      outlined
    >
      <v-card-title>
        ບົດລາຍງານ Excel ທີ່ມີຢູ່ແລ້ວ
        <v-spacer />
        <v-btn
          v-if="selected.length"
          color="error"
          class="mr-2"
          small
          :loading="deleting"
          @click="confirmDelete = true"
        >
          <v-icon
            left
            small
          >
            mdi-delete
          </v-icon>
          ລຶບທີ່ເລືອກ ({{ selected.length }})
        </v-btn>
        <v-btn
          icon
          :loading="loadingRecent"
          title="ໂຫຼດຄືນ"
          @click="fetchRecent"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-data-table
        v-model="selected"
        dense
        :headers="exportHeaders"
        :item-class="rowClass"
        :items="allExports"
        item-key="id"
        :items-per-page="10"
        :loading="loadingRecent"
        no-data-text="ຍັງບໍ່ມີບົດລາຍງານ"
        show-select
      >
        <template #[`item.data-table-select`]="{ isSelected, select, item }">
          <v-simple-checkbox
            :disabled="!isDeletable(item)"
            :ripple="false"
            :value="isSelected"
            @input="select($event)"
          />
        </template>
        <template #[`item.filters_summary`]="{ item }">
          <span
            v-if="filtersSummary(item.params)"
            :title="filtersTooltip(item.params)"
          >
            {{ filtersSummary(item.params) }}
          </span>
          <span
            v-else
            class="text--disabled"
          >
            ບໍ່ມີ
          </span>
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.status)"
            text-color="white"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #[`item.row_count`]="{ item }">
          {{ item.row_count != null ? item.row_count.toLocaleString() : '-' }}
        </template>
        <template #[`item.file_size`]="{ item }">
          {{ formatSize(item.file_size) }}
        </template>
        <template #[`item.finished_at`]="{ item }">
          {{ formatDateTime(item.finished_at) }}
        </template>
        <template #[`item.actions`]="{ item }">
          <v-progress-circular
            v-if="isInProgress(item)"
            color="primary"
            indeterminate
            size="20"
            title="ກຳລັງສ້າງ..."
            width="2"
          />
          <v-btn
            v-else
            icon
            :href="item.download_url"
            :disabled="!item.download_url"
            target="_blank"
            title="ດາວໂຫລດ"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="confirmDelete"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title>
          ຢືນຢັນການລຶບ
        </v-card-title>
        <v-card-text>
          ທ່ານຕ້ອງການລຶບ {{ selected.length }} ບົດລາຍງານ ບໍ່?
          ຂໍ້ມູນທີ່ລຶບແລ້ວຈະບໍ່ສາມາດກູ້ຄືນໄດ້.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="deleting"
            @click="confirmDelete = false"
          >
            ຍົກເລີກ
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="deleteSelected"
          >
            ລຶບ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

const EXPORT_TYPE = 'billing_payment_summary';

export default {
  name: 'BillingTotalsSummary',
  data() {
    return {
      startDate: null,
      endDate: null,
      billMonth: null,
      customerType: null,
      districtId: null,
      villageIds: [],
      districts: [],
      villages: [],
      loadingDistricts: false,
      loadingVillages: false,
      startDateMenu: false,
      endDateMenu: false,
      billMonthMenu: false,
      loading: false,
      exporting: false,
      summary: {
        total: { amount: 0, count: 0 },
        paid: { amount: 0, count: 0 },
        unpaid: { amount: 0, count: 0 },
      },
      recentExports: [],
      loadingRecent: false,
      // Tracks job IDs we have already refreshed the table for after seeing
      // them transition to `ready`. Prevents the deep `jobs` watcher from
      // re-triggering fetchRecent on every poll tick once at least one ready
      // job is present, which would otherwise make the table's loading bar
      // flash every ~3s during any active export.
      fetchedReadyJobIds: [],
      selected: [],
      confirmDelete: false,
      deleting: false,
      exportHeaders: [
        { text: 'ID', value: 'id', sortable: false },
        { text: 'ຕົວກັ່ນຕອງ', value: 'filters_summary', sortable: false },
        { text: 'ສະຖານະ', value: 'status', sortable: false },
        {
          text: 'ຈຳນວນແຖວ',
          value: 'row_count',
          align: 'end',
          sortable: false,
        },
        {
          text: 'ຂະໜາດ',
          value: 'file_size',
          align: 'end',
          sortable: false,
        },
        { text: 'ເວລາສຳເລັດ', value: 'finished_at', sortable: false },
        { text: 'ຜູ້ສ້າງ', value: 'generator_name', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'end',
        },
      ],
      customerTypes: [
        { text: 'ທັງໝົດ', value: null },
        { text: 'ລູກຄ້າຄົວເຮືອນ', value: 'home' },
        { text: 'ລູກຄ້າທຸລະກິດ', value: 'company' },
      ],
    };
  },
  computed: {
    ...mapState('exports', ['jobs']),
    allExports() {
      const vuexJobs = Object.values(this.jobs || {})
        .filter((j) => j && j.type === EXPORT_TYPE);
      const vuexIds = new Set(vuexJobs.map((j) => j.id));
      const merged = [
        ...vuexJobs,
        ...this.recentExports.filter((j) => !vuexIds.has(j.id)),
      ];
      merged.sort((a, b) => (b.id || 0) - (a.id || 0));
      return merged;
    },
  },
  watch: {
    jobs: {
      deep: true,
      handler(newJobs) {
        // Only refresh the table on a job's FIRST transition to `ready` —
        // not on every poll tick that touches vuex while a ready job
        // happens to still be in state. Otherwise loadingRecent flashes
        // every 3s during any other job's polling lifecycle.
        const newlyReady = Object.values(newJobs)
          .filter((j) => j && j.type === EXPORT_TYPE && j.status === 'ready')
          .map((j) => j.id)
          .filter((id) => !this.fetchedReadyJobIds.includes(id));
        if (newlyReady.length > 0) {
          this.fetchedReadyJobIds = [...this.fetchedReadyJobIds, ...newlyReady];
          this.fetchRecent();
        }
      },
    },
    async districtId(newId) {
      this.villages = [];
      this.villageIds = [];
      if (newId) await this.fetchVillagesForDistrict(newId);
    },
  },
  mounted() {
    this.fetchDistricts();
    this.fetchRecent();
    this.fetchData();
  },
  methods: {
    async fetchDistricts() {
      this.loadingDistricts = true;
      try {
        const res = await this.$axios.get('info/district', {
          params: { province_id: 1 },
        });
        this.districts = (res.data && res.data.data) || [];
      } catch (_) {
        this.districts = [];
      } finally {
        this.loadingDistricts = false;
      }
    },
    async fetchVillagesForDistrict(districtId) {
      this.loadingVillages = true;
      try {
        const res = await this.$axios.get(`info/district/${districtId}/village`);
        this.villages = (res.data && res.data.data) || [];
      } catch (_) {
        this.villages = [];
      } finally {
        this.loadingVillages = false;
      }
    },
    buildParams() {
      const params = {};
      if (this.startDate) params.start_date = this.startDate;
      if (this.endDate) params.end_date = this.endDate;
      if (this.billMonth) params.bill_month = this.billMonth;
      if (this.customerType) params.customer_type = this.customerType;
      if (this.villageIds && this.villageIds.length) {
        params.villages = [...this.villageIds];
      } else if (this.districtId) {
        params.district_id = this.districtId;
      }
      return params;
    },
    async fetchData() {
      this.loading = true;
      this.$store.commit('Loading_State', true);
      try {
        const res = await this.$axios.get('v2/report-billing-totals-summary', {
          params: this.buildParams(),
        });
        this.summary = res.data.data;
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'ບໍ່ມີສິດເຂົ້າເຖິງ',
          });
        } else {
          const msg = (e.response && e.response.data && e.response.data.message) || 'Network error';
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg,
          });
        }
      } finally {
        this.loading = false;
        this.$store.commit('Loading_State', false);
      }
    },
    async onExport() {
      this.exporting = true;
      try {
        await this.$store.dispatch('exports/create', {
          type: EXPORT_TYPE,
          params: this.buildParams(),
        });
        this.$store.commit('Toast_State', {
          value: true,
          color: 'success',
          msg: 'Export ໄດ້ຖືກສ້າງແລ້ວ — ກວດເບິ່ງຕາຕະລາງລຸ່ມ',
        });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Export ລົ້ມເຫຼວ';
        this.$store.commit('Toast_State', {
          value: true,
          color: 'error',
          msg,
        });
      } finally {
        this.exporting = false;
      }
    },
    async fetchRecent() {
      this.loadingRecent = true;
      try {
        const res = await this.$axios.get('v2/export-jobs', {
          params: {
            type: EXPORT_TYPE,
            status: 'queued,running,ready',
            limit: 20,
          },
        });
        this.recentExports = (res.data && res.data.data) || [];
      } catch (_) {
        this.recentExports = [];
      } finally {
        this.loadingRecent = false;
      }
    },
    async deleteSelected() {
      this.deleting = true;
      const ids = this.selected.map((j) => j.id);
      try {
        await Promise.all(
          ids.map((id) => this.$axios.delete(`v2/export-jobs/${id}`)),
        );
        ids.forEach((id) => this.$store.dispatch('exports/dismiss', id));
        this.selected = [];
        this.confirmDelete = false;
        await this.fetchRecent();
      } catch (_) {
        this.confirmDelete = false;
      } finally {
        this.deleting = false;
      }
    },
    isInProgress(item) {
      return item.status === 'queued' || item.status === 'running';
    },
    isDeletable(item) {
      return item.status === 'ready' || item.status === 'failed';
    },
    statusLabel(status) {
      const map = {
        queued: 'ກຳລັງຈັດຄິວ',
        running: 'ກຳລັງສ້າງ',
        ready: 'ພ້ອມ',
        failed: 'ຜິດພາດ',
      };
      return map[status] || status || '-';
    },
    statusColor(status) {
      if (status === 'ready') return 'success';
      if (status === 'failed') return 'error';
      if (status === 'running') return 'primary';
      return 'grey';
    },
    formatSize(bytes) {
      if (bytes == null) return '-';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDateTime(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatCurrency(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-US');
    },
    formatNumber(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-US');
    },
    filtersSummary(params) {
      if (!params) return '';
      const parts = [];
      if (params.start_date || params.end_date) {
        const from = params.start_date || '…';
        const to = params.end_date || '…';
        parts.push(`${from} → ${to}`);
      } else if (params.bill_month) {
        parts.push(params.bill_month);
      }
      if (params.customer_type) {
        parts.push(params.customer_type === 'home' ? 'ຄົວເຮືອນ' : 'ທຸລະກິດ');
      }
      if (params.villages && params.villages.length) {
        parts.push(`${params.villages.length} ບ້ານ`);
      } else if (params.district_id) {
        parts.push('1 ເມືອງ');
      }
      return parts.join(' · ');
    },
    filtersTooltip(params) {
      if (!params) return '';
      const lines = [];
      if (params.start_date) lines.push(`ຕັ້ງແຕ່: ${params.start_date}`);
      if (params.end_date) lines.push(`ຮອດ: ${params.end_date}`);
      if (params.bill_month) lines.push(`ເດືອນ: ${params.bill_month}`);
      if (params.customer_type) lines.push(`ປະເພດ: ${params.customer_type}`);
      if (params.district_id) lines.push(`ເມືອງ: ${params.district_id}`);
      if (params.villages && params.villages.length) {
        lines.push(`ບ້ານ: ${params.villages.join(', ')}`);
      }
      return lines.join('\n');
    },
    rowClass() {
      return '';
    },
  },
};
</script>
